import { Link } from "react-router-dom";
import { useUploadRides } from "../hooks/useApi";
import { UploadDropzone } from "./UploadDropzone";
import { Badge } from "./Badge";
import { Dialog } from "./Dialog";

const STATUS_TONE = {
  created: "neutral",
  duplicate: "warning",
  error: "danger",
} as const;

const STATUS_LABEL = {
  created: "Uploaded",
  duplicate: "Already uploaded",
  error: "Failed",
} as const;

export function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const mutation = useUploadRides();

  return (
    <Dialog open={open} onClose={onClose} title="Upload rides" widthClassName="max-w-xl">
      <UploadDropzone disabled={mutation.isPending} onFiles={(files) => mutation.mutate(files)} />

      {mutation.isPending && <p className="mt-4 text-sm text-slate-500 dark:text-night-400">Uploading and parsing…</p>}

      {mutation.isError && (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400">
          Upload failed: {mutation.error instanceof Error ? mutation.error.message : "unknown error"}
        </p>
      )}

      {mutation.data && (
        <ul className="mt-6 max-h-64 space-y-2 overflow-y-auto">
          {mutation.data.results.map((r) => (
            <li
              key={r.filename}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-night-800 dark:bg-night-900"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{r.filename}</div>
                {r.error && <div className="text-xs text-red-600 dark:text-red-400">{r.error}</div>}
                {r.status === "created" && r.skippedRowCount ? (
                  <div className="text-xs text-slate-500 dark:text-night-400">
                    {r.skippedRowCount} of {r.rawRowCount} rows skipped (malformed)
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                {r.rideId && (
                  <Link to={`/rides/${r.rideId}`} onClick={onClose} className="text-brand-600 hover:underline dark:text-brand-400">
                    View
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
